import { urlFor } from "@/utils/client";
import { getYouTubeId } from "@/utils/constants";
import { IMedia } from "@/utils/types";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect } from "react";
import { SliderArrows } from "./sliderArrows";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => <div className="aspect-video w-full bg-gray-200" />,
});

// Sanity asset refs encode dimensions: image-<hash>-<w>x<h>-<ext>
function getImageRatio(image: any): number | null {
  const match = image?.asset?._ref?.match(/-(\d+)x(\d+)-/);
  if (!match) return null;
  return Number(match[1]) / Number(match[2]);
}

// Average ratio of the gallery's images, clamped between 3:4 and 16:9 so
// portrait photos aren't cropped into a landscape frame (and vice versa).
function getGalleryRatio(items: IMedia["gallery"]["items"]): number {
  const ratios = items
    .map((item) => (item._type === "iwc" ? getImageRatio(item.image) : null))
    .filter((r): r is number => r !== null);
  if (ratios.length === 0) return 16 / 9;
  const avg = ratios.reduce((a, b) => a + b, 0) / ratios.length;
  return Math.min(16 / 9, Math.max(3 / 4, avg));
}

export function Media(props: IMedia) {
  const items = props.gallery?.items ?? [];
  const imageRatio = getGalleryRatio(items);

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: items.length > 1 ? true : false,
      breakpoints: {
        "(max-width: 768px)": {
          slides: {
            perView: 1,
            spacing: 30,
          },
        },
      },
      slides: {
        perView: props.gallery?.perView ? props.gallery.perView : 1,
        spacing: items.length > 1 ? 30 : 0,
      },
      slideChanged() {
        console.log("slide changed");
      },
    },
    [
      // add plugins here
    ],
  );

  useEffect(() => {
    if (items.length > 1) {
      const intervalId = setInterval(() => {
        if (instanceRef.current) {
          instanceRef.current.next();
        }
      }, 1500);

      return () => clearInterval(intervalId);
    }
  }, [items.length, instanceRef]);

  return (
    <div className="flex flex-col w-full component-gap component-padding">
      {props.title && props.description && (
        <div className="flex flex-col m-auto component-gap">
          <p className="heading text-center">{props.title}</p>
          {props.description && (
            <p className="text-center m-auto">{props.description}</p>
          )}
        </div>
      )}
      <div ref={sliderRef} className="keen-slider w-full m-auto">
        {items.map((item, i) => {
          if (item._type === "iwc" && item.image) {
            return (
              <div key={i} className="keen-slider__slide">
                <Image
                  width={1200}
                  height={Math.round(1200 / imageRatio)}
                  sizes="(max-width: 800px) 100vw, 800px"
                  style={{ aspectRatio: imageRatio }}
                  className="w-full object-cover object-center"
                  src={urlFor(item.image).url()}
                  alt={item.caption ?? ""}
                />
              </div>
            );
          } else if (item._type === "vwc" && item.playbackId) {
            return (
              <div key={i} className="keen-slider__slide">
                <h2 className="text-center text-black mx-auto mb-8">
                  {item.caption}
                </h2>
                <div className="aspect-video relative">
                  <MuxPlayer
                    streamType="on-demand"
                    playbackId={item.playbackId}
                    loop={true}
                    autoPlay={true}
                    muted={true}
                    thumbnailTime={0.1}
                    disableCookies
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              </div>
            );
          } else if (item._type === "ywc" && item.url) {
            const videoId = getYouTubeId(item.url);
            if (!videoId) return null;
            const ytParams = item.autoplay
              ? `?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&modestbranding=1&rel=0&showinfo=0`
              : `?rel=0`;
            return (
              <div key={i} className="keen-slider__slide">
                {item.caption && (
                  <h2 className="text-center text-black mx-auto mb-8">
                    {item.caption}
                  </h2>
                )}
                <div className="aspect-video relative w-full">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${videoId}${ytParams}`}
                    title={item.caption ?? "YouTube video"}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-0"
                  />
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
      {items.length > 1 && (
        <SliderArrows
          onPrev={() => instanceRef.current?.prev()}
          onNext={() => instanceRef.current?.next()}
        />
      )}
    </div>
  );
}
